import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Fade,
  Zoom,
  Grow,
  keyframes,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// ─── i18n ─────────────────────────────────────────────────────────────────────

const TRANSLATIONS = {
  en: {
    title: 'Excellent!',
    subtitle: 'Your survey has been submitted successfully',
    thankYou: 'Thank you so much for your time!',
    body: 'Your opinion is extremely valuable to us. Every response helps us better understand your needs and improve our services to offer you the best possible experience.',
    completed: 'Completed',
    saved: 'Saved',
    contact: '📧 Have questions? Contact us anytime',
    createdBy: 'Created with 💜 by',
  },
  es: {
    title: '¡Excelente!',
    subtitle: 'Tu encuesta ha sido enviada exitosamente',
    thankYou: '¡Muchas gracias por tu tiempo!',
    body: 'Tu opinión es extremadamente valiosa para nosotros. Cada respuesta nos ayuda a entender mejor tus necesidades y a mejorar nuestros servicios para ofrecerte la mejor experiencia posible.',
    completed: 'Completado',
    saved: 'Guardado',
    contact: '📧 ¿Tienes preguntas? Contáctanos en cualquier momento',
    createdBy: 'Creado con 💜 por',
  },
};

/**
 * Detect browser language. Returns 'es' for Spanish speakers, 'en' for everyone else.
 * Iterates navigator.languages (ordered by preference) before falling back to navigator.language.
 */
const detectLocale = () => {
  const langs = navigator.languages?.length
    ? navigator.languages
    : [navigator.language || 'en'];

  for (const lang of langs) {
    if (lang.toLowerCase().startsWith('es')) return 'es';
    if (lang.toLowerCase().startsWith('en')) return 'en';
  }
  return 'en';
};

// ─── Constants ────────────────────────────────────────────────────────────────

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b', '#fa709a', '#fee140', '#30cfd0'];
const EMOJIS = ['🎉', '🎊', '✨', '🙌', '💚', '⭐', '🎈'];
const CONFETTI_COUNT = 150;

const BOUNCE_ANIMATIONS = [
  { y: -25, r: 15 },
  { y: -20, r: -10 },
  { y: -30, r: 20 },
  { y: -18, r: -15 },
  { y: -22, r: 12 },
  { y: -28, r: -18 },
  { y: -24, r: 8 },
].map(({ y, r }) => keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50%       { transform: translateY(${y}px) rotate(${r}deg); }
`);

// ─── Keyframes ────────────────────────────────────────────────────────────────

const gradientShift = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50%       { background-position: 100% 50%; }
`;

const confettiFall = keyframes`
  0%   { transform: translateY(-100vh) rotate(0deg);   opacity: 1; }
  100% { transform: translateY(100vh)  rotate(720deg); opacity: 0; }
`;

const rainbowSlide = keyframes`
  0%   { background-position: 0% 0%;   }
  100% { background-position: 200% 0%; }
`;

const successPop = keyframes`
  0%, 100% { transform: scale(1)   rotate(0deg); }
  50%       { transform: scale(1.1) rotate(5deg); }
`;

const glowPulse = keyframes`
  0%, 100% { transform: translate(-50%, -50%) scale(1);   opacity: 0.6; }
  50%       { transform: translate(-50%, -50%) scale(1.2); opacity: 0.9; }
`;

const titleShimmer = keyframes`
  0%, 100% { filter: brightness(1);   }
  50%       { filter: brightness(1.2); }
`;

const twinkle = keyframes`
  0%, 100% { opacity: 1;   transform: scale(1);   }
  50%       { opacity: 0.5; transform: scale(0.8); }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const MainContainer = styled(Box)({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
  padding: '2rem 0',
  background: '#1a1a2e',
});

const AnimatedGradient = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(45deg, #667eea, #764ba2, #f093fb, #4facfe, #43e97b)',
  backgroundSize: '400% 400%',
  animation: `${gradientShift} 15s ease infinite`,
  zIndex: 0,
});

const ConfettiWrapper = styled(Box)({
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  zIndex: 1,
  pointerEvents: 'none',
});

const ConfettiPiece = styled(Box, {
  shouldForwardProp: (prop) => !['delay', 'pieceColor'].includes(prop),
})(({ delay, pieceColor }) => ({
  position: 'absolute',
  width: 10,
  height: 10,
  top: -10,
  opacity: 0,
  backgroundColor: pieceColor,
  animation: `${confettiFall} 5s linear infinite`,
  animationDelay: `${delay}s`,
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6, 4),
  borderRadius: 32,
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  boxShadow: '0 30px 90px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.3) inset',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 8,
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 25%, #f093fb 50%, #4facfe 75%, #43e97b 100%)',
    backgroundSize: '200% 100%',
    animation: `${rainbowSlide} 3s linear infinite`,
  },
  [theme.breakpoints.down('md')]: { padding: theme.spacing(4, 3), borderRadius: 24 },
  [theme.breakpoints.down('sm')]: { padding: theme.spacing(3, 2) },
}));

const SuccessIconContainer = styled(Box)({ position: 'relative', display: 'inline-block' });

const SuccessIcon = styled(CheckCircleIcon)(({ theme }) => ({
  fontSize: 140,
  color: '#4caf50',
  position: 'relative',
  zIndex: 2,
  animation: `${successPop} 2s ease-in-out infinite`,
  filter: 'drop-shadow(0 0 20px rgba(76,175,80,0.6))',
  [theme.breakpoints.down('md')]: { fontSize: 100 },
  [theme.breakpoints.down('sm')]: { fontSize: 80 },
}));

const SuccessGlow = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 160,
  height: 160,
  background: 'radial-gradient(circle, rgba(76,175,80,0.4) 0%, transparent 70%)',
  borderRadius: '50%',
  animation: `${glowPulse} 2s ease-in-out infinite`,
});

const Title = styled(Typography)(({ theme }) => ({
  fontWeight: 900,
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  marginBottom: theme.spacing(2),
  fontSize: '4.5rem',
  letterSpacing: -3,
  animation: `${titleShimmer} 3s ease-in-out infinite`,
  [theme.breakpoints.down('md')]: { fontSize: '3rem' },
  [theme.breakpoints.down('sm')]: { fontSize: '2.5rem' },
}));

const DividerStar = styled('span')({
  fontSize: '1.5rem',
  animation: `${twinkle} 2s ease-in-out infinite`,
});

const DividerLine = styled('span')({
  width: 100,
  height: 2,
  background: 'linear-gradient(90deg, transparent, #764ba2, transparent)',
});

const CelebrationEmoji = styled('span', {
  shouldForwardProp: (prop) => prop !== 'animationIndex',
})(({ animationIndex }) => ({
  fontSize: '3.5rem',
  display: 'inline-block',
  cursor: 'pointer',
  transition: 'transform 0.3s ease',
  animation: `${BOUNCE_ANIMATIONS[animationIndex % BOUNCE_ANIMATIONS.length]} 2s ease-in-out infinite`,
  '&:hover': { transform: 'scale(1.3) rotate(15deg)' },
  '@media (max-width: 768px)': { fontSize: '2.5rem' },
  '@media (max-width: 480px)': { fontSize: '2rem' },
}));

const StatsBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(4),
  padding: theme.spacing(3),
  margin: theme.spacing(3, 0),
  background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
  borderRadius: 16,
  border: '2px solid rgba(102,126,234,0.2)',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
}));

const StatDivider = styled(Box)(({ theme }) => ({
  width: 2,
  height: 60,
  background: 'linear-gradient(180deg, transparent, #764ba2, transparent)',
  [theme.breakpoints.down('sm')]: { width: 60, height: 2 },
}));

// ─── Component ────────────────────────────────────────────────────────────────

const ThanksUser = () => {
  const [showContent, setShowContent] = useState(false);
  const [showEmojis, setShowEmojis]   = useState(false);

  // Resolve translations once — detectLocale() reads navigator, safe on mount
  const t = useMemo(() => TRANSLATIONS[detectLocale()], []);

  const confetti = useMemo(() =>
    Array.from({ length: CONFETTI_COUNT }, (_, id) => ({
      id,
      left: Math.random() * 100,
      delay: Math.random() * 3,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
    })),
  []);

  useEffect(() => {
    const t1 = setTimeout(() => setShowContent(true), 100);
    const t2 = setTimeout(() => setShowEmojis(true),  600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <MainContainer>
      <ConfettiWrapper aria-hidden="true">
        {confetti.map(({ id, left, delay, color }) => (
          <ConfettiPiece key={id} delay={delay} pieceColor={color} sx={{ left: `${left}%` }} />
        ))}
      </ConfettiWrapper>

      <AnimatedGradient />

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
        <Zoom in={showContent} timeout={800}>
          <StyledPaper elevation={6}>

            {/* Icon */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Grow in={showContent} timeout={1000}>
                <SuccessIconContainer>
                  <SuccessIcon />
                  <SuccessGlow />
                </SuccessIconContainer>
              </Grow>
            </Box>

            {/* Title */}
            <Fade in={showContent} timeout={1200}>
              <Title variant="h2" component="h1">{t.title}</Title>
            </Fade>

            {/* Subtitle */}
            <Fade in={showContent} timeout={1400}>
              <Typography variant="h5" sx={{ color: '#424242', fontWeight: 600, mb: 3, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
                {t.subtitle}
              </Typography>
            </Fade>

            {/* Divider */}
            <Fade in={showContent} timeout={1600}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, my: 3 }}>
                <DividerStar>✨</DividerStar>
                <DividerLine />
                <DividerStar>✨</DividerStar>
              </Box>
            </Fade>

            {/* Body */}
            <Fade in={showContent} timeout={1800}>
              <Typography
                variant="body1"
                sx={{
                  color: '#616161',
                  lineHeight: 1.9,
                  mb: 4,
                  maxWidth: 650,
                  mx: 'auto',
                  fontSize: { xs: '1rem', md: '1.15rem' },
                  '& strong': { color: '#424242', fontSize: { xs: '1.15rem', md: '1.3rem' } },
                }}
              >
                <strong>{t.thankYou}</strong>
                <br /><br />
                {t.body}
              </Typography>
            </Fade>

            {/* Emojis */}
            {showEmojis && (
              <Box
                role="img"
                aria-label={t.title}
                sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1.5, md: 2 }, my: 4, flexWrap: 'wrap' }}
              >
                {EMOJIS.map((emoji, index) => (
                  <Grow key={emoji} in timeout={800} style={{ transitionDelay: `${index * 100}ms` }}>
                    <CelebrationEmoji animationIndex={index}>{emoji}</CelebrationEmoji>
                  </Grow>
                ))}
              </Box>
            )}

            {/* Stats */}
            <Fade in={showContent} timeout={2000}>
              <StatsBox>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#667eea', fontSize: { xs: '2rem', md: '2.5rem' }, lineHeight: 1 }}>
                    100%
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#757575', fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {t.completed}
                  </Typography>
                </Box>

                <StatDivider />

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircleIcon sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, color: '#4caf50' }} />
                  <Typography variant="caption" sx={{ color: '#757575', fontSize: '0.9rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {t.saved}
                  </Typography>
                </Box>
              </StatsBox>
            </Fade>

            {/* Footer note */}
            <Fade in={showContent} timeout={2400}>
              <Box sx={{ mt: 3, pt: 3, borderTop: '2px dashed #e0e0e0' }}>
                <Typography variant="body2" sx={{ color: '#757575', fontSize: '1rem', fontWeight: 500 }}>
                  {t.contact}
                </Typography>
              </Box>
            </Fade>

          </StyledPaper>
        </Zoom>
      </Container>

      {/* Attribution */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 3,
          background: 'rgba(0,0,0,0.3)',
          backdropFilter: 'blur(10px)',
          padding: '8px 24px',
          borderRadius: '50px',
          border: '1px solid rgba(255,255,255,0.2)',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,1)',
            fontSize: '0.95rem',
            fontWeight: 500,
            '& a': {
              color: 'inherit',
              textDecoration: 'none',
              fontWeight: 700,
              transition: 'all 0.3s ease',
              borderBottom: '2px solid transparent',
              paddingBottom: '2px',
              '&:hover': { borderBottomColor: 'rgba(255,255,255,1)', textShadow: '0 0 10px rgba(255,255,255,0.8)' },
            },
          }}
        >
          {t.createdBy}{' '}
          <a href="https://emojisurveys.pro" target="_blank" rel="noopener noreferrer">
            emojisurveys.pro
          </a>
        </Typography>
      </Box>
    </MainContainer>
  );
};

export default ThanksUser;